import { useState } from 'react';
import { Sparkles, Link as LinkIcon, Gift, Image as ImageIcon, Copy, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { generateAIContent } from '../services/aiService';
import { SuccessModal } from './SuccessModal';

interface LinkCreationFormProps {
  mergedImage: File | null;
  onLinkCreated: () => void;
  onClearImage: () => void;
}

export function LinkCreationForm({ mergedImage, onLinkCreated, onClearImage }: LinkCreationFormProps) {
  const { user } = useAuth();
  const [keyword, setKeyword] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fbCaption, setFbCaption] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [bonusLink, setBonusLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [captionCopied, setCaptionCopied] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdShortUrl, setCreatedShortUrl] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const autoGenerateContent = async () => {
    if (!keyword.trim()) {
      alert('Please enter a keyword first');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const videoKeywords = ['video', 'nonton', 'lihat', 'tonton', 'celebrity', 'artis', 'gosip'];
      const isVideo = videoKeywords.some(k => keyword.toLowerCase().includes(k));
      const contentType = isVideo ? 'video' : 'news';

      const prompt = `Indonesian viral social media content for: "${keyword}".
Rules:
- TITLE: Short clickbait (max 70 chars). ${isVideo ? 'Include "Video", "Nonton", or "Lihat"' : 'Shocking and curiosity-inducing'}
- DESCRIPTION: Hook sentence (max 150 chars) creating urgency
- CAPTION: Facebook post (200-400 chars) with natural tone, 2-4 emojis, 3-5 hashtags, FOMO, call to action
All in Indonesian language.`;

      const generated = await generateAIContent(contentType, prompt);

      setTitle(generated.title || '');
      setDescription(generated.description || '');
      setFbCaption(generated.caption || '');
    } catch (err) {
      console.error('AI generation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate content';
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const copyFbCaption = () => {
    navigator.clipboard.writeText(fbCaption);
    setCaptionCopied(true);
    setTimeout(() => setCaptionCopied(false), 2000);
  };

  const generateShortCode = () => {
    return Math.random().toString(36).substring(2, 8);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError('');
    setLoading(true);

    try {
      let imageUrl = '';

      if (mergedImage) {
        const fileName = `${Date.now()}-${mergedImage.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('images')
          .upload(fileName, mergedImage, {
            contentType: 'image/jpeg',
            upsert: false,
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('images')
          .getPublicUrl(uploadData.path);

        imageUrl = publicUrl;
      }

      // JURUS LINK CANTIK: Pakai Custom Slug kalau ada, kalau kosong pakai Acak
      const shortCode = customSlug || generateShortCode();

      const { error: insertError } = await supabase
        .from('masked_links')
        .insert({
          title,
          description,
          target_url: targetUrl,
          bonus_link: bonusLink || '',
          og_image: imageUrl,
          short_code: shortCode,
          clicks: 0,
          user_id: user.id,
        });

      if (insertError) throw insertError;

      const shortUrl = `${window.location.origin}/${shortCode}`;
      setCreatedShortUrl(shortUrl);
      setShowSuccessModal(true);
      onLinkCreated();
    } catch (err) {
      console.error('Error creating link:', err);
      setError(err instanceof Error ? err.message : 'Failed to create link');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setKeyword('');
    setTitle('');
    setDescription('');
    setFbCaption('');
    setTargetUrl('');
    setBonusLink('');
    onClearImage();
  };

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
      <h2 className="text-xl font-bold text-white mb-6">Link Details</h2>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {mergedImage && (
        <div className="mb-6 bg-slate-800 rounded-lg p-4 flex items-center gap-3">
          <ImageIcon className="w-5 h-5 text-emerald-500" />
          <span className="text-slate-300 text-sm flex-1">
            Canvas image ready ({(mergedImage.size / 1024).toFixed(0)}KB)
          </span>
          <button
            onClick={onClearImage}
            className="text-slate-500 hover:text-white text-sm"
          >
            Remove
          </button>
        </div>
      )}

      <div className="mb-6 bg-gradient-to-br from-blue-900/20 to-emerald-900/20 border border-blue-700/30 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-blue-400" />
          <h3 className="text-white font-semibold">Smart AI Generator</h3>
        </div>
        <p className="text-slate-400 text-xs mb-4">
          Enter a keyword and let AI generate viral content for you
        </p>
        <div className="flex gap-3">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="Enter keyword (e.g., Amanda Manopo, Diskon Sepatu...)"
          />
          <button
            type="button"
            onClick={autoGenerateContent}
            disabled={generating || !keyword.trim()}
            className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25 flex items-center gap-2 whitespace-nowrap"
          >
            <Sparkles className="w-5 h-5" />
            {generating ? 'Generating...' : 'Auto-Generate'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* --- INPUT LINK CANTIK (CUSTOM SLUG) --- */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-300">Link Cantik (Opsional)</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500">/</span>
          </div>
          <input
            type="text"
            value={customSlug}
            onChange={(e) => setCustomSlug(e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''))}
            placeholder="contoh: video-viral-2025"
            className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-7 pr-4 text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
          />
        </div>
        <p className="text-xs text-gray-500">Biarkan kosong jika ingin kode acak otomatis.</p>
      </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="Generated title will appear here..."
            required
            maxLength={120}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
            placeholder="Generated description will appear here..."
            rows={3}
            required
            maxLength={300}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-300">
              Facebook Caption
            </label>
            {fbCaption && (
              <button
                type="button"
                onClick={copyFbCaption}
                className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition"
              >
                {captionCopied ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy Caption
                  </>
                )}
              </button>
            )}
          </div>
          <textarea
            value={fbCaption}
            onChange={(e) => setFbCaption(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
            placeholder="Generated Facebook caption with emojis and hashtags will appear here..."
            rows={5}
          />
          <p className="text-slate-500 text-xs mt-2">
            This caption is optimized for Facebook posts with emojis and hashtags
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            <LinkIcon className="w-4 h-4 inline mr-2" />
            Target URL (Main Affiliate Link)
          </label>
          <input
            type="url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="https://example.com/affiliate-link"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            <Gift className="w-4 h-4 inline mr-2" />
            Bonus URL (Optional)
          </label>
          <input
            type="url"
            value={bonusLink}
            onChange={(e) => setBonusLink(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            placeholder="https://example.com/bonus-file"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25"
        >
          {loading ? 'Creating Link...' : 'Create Link'}
        </button>
      </form>

      {showSuccessModal && (
        <SuccessModal
          shortUrl={createdShortUrl}
          onClose={() => setShowSuccessModal(false)}
          onCreateNew={handleCreateNew}
        />
      )}
    </div>
  );
}
